/**
 * FVG Scanner — live NIFTY-only paper trading for the Fair Value Gap strategy.
 * Limit entry at the 50% gap level, stop beyond the gap. Scans every 5 min.
 * (Backtest showed FVG has an edge on NIFTY only — hence NIFTY-only here.)
 */

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = "/api/zerodha";

function confColor(label) {
  return { "A+": "#34d399", A: "#60a5fa", B: "#f59e0b", C: "#f87171" }[label] || "var(--grey-text2)";
}
function confTitle(t) {
  if (!Array.isArray(t.confluence) || !t.confluence.length) return "No confidence breakdown";
  return t.confluence.map(f => `${f.name}: ${f.score}/${f.max} — ${f.detail}`).join("\n");
}
function ConfBadge({ t }) {
  if (t.confidence == null) return <span style={{ color: "var(--edge)" }}>-</span>;
  const color = confColor(t.confidence_label);
  return (
    <span title={confTitle(t)} style={{ cursor: "help", display: "inline-block", padding: "1px 6px", borderRadius: 3, fontSize: 9, fontWeight: 700, background: color + "22", color, border: `1px solid ${color}` }}>
      {t.confidence} {t.confidence_label}
    </span>
  );
}

function round(n) { return Math.round((n || 0) * 10) / 10; }
function targetPoints(entry, target, dir) {
  if (entry == null || target == null) return 0;
  return round(dir === "BULLISH" ? target - entry : entry - target);
}

function Stat({ label, value, color }) {
  return (
    <div style={{ padding: "6px 12px", background: "#11182a", borderRadius: 6, border: "1px solid var(--panel-hover)", minWidth: 90 }}>
      <div style={{ fontSize: 10, color: "#8aa", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: color || "#e5e7eb" }}>{value}</div>
    </div>
  );
}

// Inline SVG equity curve from the backtest's cumulative-pnl points.
function EquitySpark({ equity }) {
  if (!equity || equity.length < 2) return null;
  const w = 560, h = 90, pad = 4;
  const ys = equity.map(e => e.pnl);
  const min = Math.min(0, ...ys), max = Math.max(0, ...ys);
  const span = max - min || 1;
  const x = i => pad + (i / (equity.length - 1)) * (w - 2 * pad);
  const y = v => h - pad - ((v - min) / span) * (h - 2 * pad);
  const path = equity.map((e, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(e.pnl).toFixed(1)}`).join(" ");
  const zeroY = y(0);
  const last = ys[ys.length - 1];
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} style={{ background: "#0c1320", borderRadius: 6, border: "1px solid var(--panel-hover)" }}>
      <line x1={pad} y1={zeroY} x2={w - pad} y2={zeroY} stroke="#334" strokeDasharray="3 3" />
      <path d={path} fill="none" stroke={last >= 0 ? "#34d399" : "#f87171"} strokeWidth="1.5" />
    </svg>
  );
}

function exportCSV(trades) {
  if (!trades.length) return;
  const h = "Direction,Confidence,Conf Label,Entry Time,Exit Time,Entry,Exit,SL,T1,T2,Risk,Gap Pts,Gap ATR,P&L Pts,R,Result,Exit Reason\n";
  const rows = trades.map(t =>
    `${t.direction},${t.confidence ?? ""},${t.confidence_label || ""},${t.entry_time},${t.exit_time || ""},${t.entry_spot},${t.exit_spot || ""},${t.sl_spot},${t.t1_spot},${t.t2_spot},${t.risk_pts},${t.gap_pts},${t.gap_atr},${t.pnl_pts || 0},${t.r_multiple || 0},${t.result || "OPEN"},${t.exit_reason || ""}`
  ).join("\n");
  const blob = new Blob([h + rows], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "fvg-scanner-trades.csv"; a.click();
}

export default function FvgScanner() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const [bt, setBt] = useState(null);
  const [btLoading, setBtLoading] = useState(false);
  const [btErr, setBtErr] = useState("");
  const [btCfg, setBtCfg] = useState({ days: 90, min_gap_atr: 1.0, sl_buffer: 0.25, t1r: 1.5, t2r: 3, cost_pts: 10 });

  const poll = async () => {
    try {
      const res = await axios.get(`${API}/fvg-scanner/status`, { timeout: 10000 });
      if (res.data.success) setStatus(res.data);
    } catch {}
  };

  useEffect(() => {
    poll();
    pollRef.current = setInterval(poll, 10000);
    return () => clearInterval(pollRef.current);
  }, []);

  const start = async () => { setLoading(true); try { await axios.post(`${API}/fvg-scanner/start`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const stop = async () => { setLoading(true); try { await axios.post(`${API}/fvg-scanner/stop`); } catch {} setTimeout(poll, 1000); setLoading(false); };
  const clear = async () => { if (!window.confirm("Clear all closed FVG trades?")) return; try { await axios.post(`${API}/fvg-scanner/clear`); } catch {} setTimeout(poll, 500); };

  const runBacktest = async () => {
    setBtLoading(true); setBtErr(""); setBt(null);
    try {
      const res = await axios.get(`${API}/fvg-backtest`, { params: { index: "NIFTY", ...btCfg }, timeout: 180000 });
      if (res.data.success) setBt(res.data.data);
      else setBtErr(res.data.message || "Backtest failed");
    } catch (e) {
      setBtErr(e?.response?.data?.message || e.message || "Request failed");
    }
    setBtLoading(false);
  };
  const setCfg = (k, v) => setBtCfg(c => ({ ...c, [k]: v }));

  const trades = status?.trades || [];
  const openTrades = trades.filter(t => t.status === "OPEN");
  const closedTrades = trades.filter(t => t.status === "CLOSED");
  const pending = status?.pendingOrders || [];

  return (
    <div className="five-ema">
      <div className="dashboard-header">
        <div>
          <h2>FVG Scanner <span style={{ fontSize: 12, color: "#a78bfa" }}>(NIFTY only)</span></h2>
          <p className="dashboard-subtitle">Live paper trading — Fair Value Gap, limit entry at 50% level, stop beyond the gap | P&L in points | Conf = setup quality (hover for breakdown): A+ ≥80, A ≥65, B ≥50, C below</p>
        </div>
        <div className="dashboard-actions">
          <button className={`btn ${status?.active ? "btn-danger" : "btn-primary"}`} onClick={status?.active ? stop : start} disabled={loading}>
            {status?.active ? "Stop Scanner" : "Start Scanner"}
          </button>
        </div>
      </div>

      {status && (
        <div style={{ display: "flex", gap: 16, padding: "8px 12px", background: status.active ? "var(--tint-pos)" : "var(--panel3)", borderBottom: "1px solid var(--surface3)", fontSize: 12, color: "var(--grey-text)", flexWrap: "wrap", alignItems: "center" }}>
          <span>{status.active ? <span style={{ color: "#34d399" }}>SCANNING</span> : "STOPPED"}</span>
          <span>Scans: {status.scanCount}</span>
          {status.lastScanTime && <span>Last: {new Date(status.lastScanTime).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}</span>}
          <span>Market: {status.marketOpen ? <span style={{ color: "#34d399" }}>OPEN</span> : <span style={{ color: "#f87171" }}>CLOSED</span>}</span>
          <span>Armed: <strong>{status.pending}</strong></span>
          <span>Open: <strong>{status.openTrades}</strong></span>
          <span>WR: <strong>{status.winRate}%</strong> ({status.wins}/{status.closed})</span>
          <span style={{ color: "var(--edge)" }}>|</span>
          <span>P&L: <strong className={status.totalPnl >= 0 ? "positive" : "negative"}>{status.totalPnl > 0 ? "+" : ""}{status.totalPnl} pts</strong></span>
        </div>
      )}

      {/* Backtest panel */}
      <div style={{ margin: "10px 12px", padding: 12, borderRadius: 8, background: "#0f1626", border: "1px solid var(--panel-hover)" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#a78bfa", marginRight: 6 }}>Backtest <span style={{ fontSize: 11, color: "#8aa" }}>(NIFTY, same engine as live)</span></div>
          {[["days", "Days", 1], ["min_gap_atr", "Min gap (ATR)", 0.05], ["sl_buffer", "SL buffer (ATR)", 0.05], ["t1r", "T1 (R)", 0.5], ["t2r", "T2 (R)", 0.5], ["cost_pts", "Cost (pts/trade)", 1]].map(([k, lbl, step]) => (
            <label key={k} style={{ fontSize: 10, color: "#8aa", display: "flex", flexDirection: "column", gap: 2 }}>
              {lbl}
              <input type="number" step={step} value={btCfg[k]} onChange={e => setCfg(k, e.target.value === "" ? "" : Number(e.target.value))}
                style={{ width: 80, padding: "4px 6px", background: "#0c1320", border: "1px solid #334", borderRadius: 4, color: "#e5e7eb", fontSize: 12 }} />
            </label>
          ))}
          <button className="btn btn-primary" onClick={runBacktest} disabled={btLoading}>{btLoading ? "Running…" : "Run Backtest"}</button>
        </div>

        {btErr && <div style={{ marginTop: 8, color: "#f87171", fontSize: 12 }}>⚠ {btErr}</div>}

        {bt && bt.summary && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Stat label="Trades" value={bt.summary.totalTrades} />
              <Stat label="Win rate" value={`${bt.summary.winRate}%`} />
              <Stat label={`Net P&L (−${bt.summary.costPts}pt/trade)`} value={`${bt.summary.totalPnlPts > 0 ? "+" : ""}${bt.summary.totalPnlPts}`} color={bt.summary.totalPnlPts >= 0 ? "#34d399" : "#f87171"} />
              <Stat label="Gross P&L" value={`${bt.summary.grossPnlPts > 0 ? "+" : ""}${bt.summary.grossPnlPts}`} color="#8aa" />
              <Stat label="Profit factor" value={bt.summary.profitFactor} color={bt.summary.profitFactor >= 1 ? "#34d399" : "#f87171"} />
              <Stat label="Avg R" value={bt.summary.avgR} color={bt.summary.avgR >= 0 ? "#34d399" : "#f87171"} />
              <Stat label="Max DD" value={`${bt.summary.maxDrawdownPts} pts`} color="#f87171" />
              <Stat label="Bull / Bear" value={`${bt.summary.bullTrades} / ${bt.summary.bearTrades}`} />
              <Stat label="FVGs seen" value={bt.totalFvgs} />
            </div>

            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 10, color: "#8aa", marginBottom: 3 }}>Equity curve (cumulative points)</div>
              <EquitySpark equity={bt.equity} />
            </div>

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 12 }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--grey-text)", marginBottom: 4 }}>By gap size</div>
                <table className="stocks-table" style={{ fontSize: 11, width: "100%" }}>
                  <thead><tr><th>Bucket</th><th>Trades</th><th>WR</th><th>P&L</th></tr></thead>
                  <tbody>
                    {Object.entries(bt.summary.gapStats || {}).map(([k, v]) => (
                      <tr key={k}><td>{k}</td><td>{v.trades}</td><td>{v.trades ? Math.round(v.wins / v.trades * 100) : 0}%</td>
                        <td className={v.pnl >= 0 ? "positive" : "negative"}>{v.pnl > 0 ? "+" : ""}{v.pnl}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--grey-text)", marginBottom: 4 }}>By exit reason</div>
                <table className="stocks-table" style={{ fontSize: 11, width: "100%" }}>
                  <thead><tr><th>Reason</th><th>Trades</th><th>WR</th><th>P&L</th></tr></thead>
                  <tbody>
                    {Object.entries(bt.summary.exitStats || {}).map(([k, v]) => (
                      <tr key={k}><td>{k}</td><td>{v.trades}</td><td>{v.trades ? Math.round(v.wins / v.trades * 100) : 0}%</td>
                        <td className={v.pnl >= 0 ? "positive" : "negative"}>{v.pnl > 0 ? "+" : ""}{v.pnl}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: 11, color: "#8aa" }}>All {bt.trades?.length || 0} backtest trades</summary>
              <div className="stocks-table-wrapper" style={{ marginTop: 6, maxHeight: 320, overflow: "auto" }}>
                <table className="stocks-table" style={{ fontSize: 11 }}>
                  <thead><tr><th>Dir</th><th>Entry Time</th><th>Entry</th><th>Exit</th><th>SL</th><th>Gap</th><th>P&L</th><th>R</th><th>Result</th><th>Exit Reason</th></tr></thead>
                  <tbody>
                    {(bt.trades || []).map((t, i) => (
                      <tr key={i} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                        <td><span className={`signal-badge ${t.direction === "BULLISH" ? "badge-long" : "badge-short"}`}>{t.direction === "BULLISH" ? "BULL" : "BEAR"}</span></td>
                        <td style={{ fontSize: 10 }}>{t.entry_time ? new Date(t.entry_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                        <td>{t.entry_spot}</td><td>{t.exit_spot}</td><td className="sl">{t.sl_spot}</td>
                        <td style={{ fontSize: 10 }}>{t.gap_pts}pt/{t.gap_atr}ATR</td>
                        <td className={t.pnl_pts >= 0 ? "positive" : "negative"}>{t.pnl_pts > 0 ? "+" : ""}{t.pnl_pts}</td>
                        <td style={{ color: t.r_multiple >= 0 ? "#34d399" : "#f87171" }}>{(t.r_multiple || 0).toFixed(1)}R</td>
                        <td><span style={{ color: t.result === "WIN" ? "#34d399" : "#f87171", fontWeight: 700 }}>{t.result}</span></td>
                        <td style={{ fontSize: 10 }}>{t.exit_reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </div>
        )}
      </div>

      {/* Armed limit orders */}
      {pending.length > 0 && (
        <div style={{ padding: "8px 12px" }}>
          <h4 style={{ margin: "0 0 6px", fontSize: 13, color: "#a78bfa" }}>Armed Limit Orders ({pending.length})</h4>
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Dir</th><th>Limit @ (50%)</th><th>SL</th><th>T1</th><th>T2</th><th>Risk</th><th>Gap Pts</th><th>Gap ATR</th><th>Armed</th></tr></thead>
              <tbody>
                {pending.map((po, i) => (
                  <tr key={i}>
                    <td><span className={`signal-badge ${po.direction === "BULLISH" ? "badge-long" : "badge-short"}`}>{po.direction === "BULLISH" ? "BULL" : "BEAR"}</span></td>
                    <td><strong>{po.entry_spot}</strong></td>
                    <td className="sl">{po.sl_spot}</td>
                    <td>{po.t1_spot}</td>
                    <td>{po.t2_spot}</td>
                    <td>{po.risk_pts} pts</td>
                    <td>{po.gap_pts}</td>
                    <td>{po.gap_atr}</td>
                    <td style={{ fontSize: 10 }}>{po.armed_time ? new Date(po.armed_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Open trades */}
      {openTrades.length > 0 && (
        <div style={{ padding: "8px 12px" }}>
          <h4 style={{ margin: "0 0 6px", fontSize: 13, color: "#f59e0b" }}>Open Trades ({openTrades.length})</h4>
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Conf</th><th>Dir</th><th>Entry Time</th><th>Entry</th><th>SL</th><th>T1</th><th>T1 Pts</th><th>T2</th><th>T2 Pts</th><th>Risk</th><th>Gap</th><th>MFE</th><th>MAE</th><th>T1 Hit</th></tr></thead>
              <tbody>
                {openTrades.map(t => (
                  <tr key={t.entry_time} className="trade-open-row">
                    <td><ConfBadge t={t} /></td>
                    <td><span className={`signal-badge ${t.direction === "BULLISH" ? "badge-long" : "badge-short"}`}>{t.direction === "BULLISH" ? "BULL" : "BEAR"}</span></td>
                    <td style={{ fontSize: 10 }}>{t.entry_time ? new Date(t.entry_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                    <td><strong>{t.entry_spot}</strong></td>
                    <td className="sl">{t.sl_spot}</td>
                    <td>{t.t1_spot}</td>
                    <td style={{ color: "#34d399" }}>+{targetPoints(t.entry_spot, t.t1_spot, t.direction)}</td>
                    <td>{t.t2_spot}</td>
                    <td style={{ color: "#34d399" }}>+{targetPoints(t.entry_spot, t.t2_spot, t.direction)}</td>
                    <td>{t.risk_pts} pts</td>
                    <td style={{ fontSize: 10 }}>{t.gap_pts}pt/{t.gap_atr}ATR</td>
                    <td style={{ color: "#34d399" }}>{t.max_favorable_pts}</td>
                    <td style={{ color: "#f87171" }}>{t.max_adverse_pts}</td>
                    <td>{t.t1_hit ? "YES" : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Closed trades */}
      <div style={{ padding: "8px 12px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h4 style={{ margin: 0, fontSize: 13, color: "var(--grey-text)" }}>Closed Trades ({closedTrades.length})</h4>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-export" onClick={() => exportCSV(trades)}>Export CSV</button>
            {closedTrades.length > 0 && <button className="btn btn-sm btn-danger" onClick={clear}>Clear</button>}
          </div>
        </div>
        {closedTrades.length > 0 && (
          <div className="stocks-table-wrapper">
            <table className="stocks-table" style={{ fontSize: 11 }}>
              <thead><tr><th>Conf</th><th>Dir</th><th>Entry</th><th>Exit</th><th>Entry Spot</th><th>Exit Spot</th><th>SL</th><th>Risk</th><th>Gap</th><th>P&L</th><th>R</th><th>MFE</th><th>MAE</th><th>Result</th><th>Exit Reason</th></tr></thead>
              <tbody>
                {closedTrades.map(t => (
                  <tr key={t.entry_time} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                    <td><ConfBadge t={t} /></td>
                    <td><span className={`signal-badge ${t.direction === "BULLISH" ? "badge-long" : "badge-short"}`}>{t.direction === "BULLISH" ? "BULL" : "BEAR"}</span></td>
                    <td style={{ fontSize: 10 }}>{t.entry_time ? new Date(t.entry_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                    <td style={{ fontSize: 10 }}>{t.exit_time ? new Date(t.exit_time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "-"}</td>
                    <td>{t.entry_spot}</td>
                    <td>{t.exit_spot}</td>
                    <td className="sl">{t.sl_spot}</td>
                    <td>{t.risk_pts} pts</td>
                    <td style={{ fontSize: 10 }}>{t.gap_pts}pt/{t.gap_atr}ATR</td>
                    <td className={t.pnl_pts >= 0 ? "positive" : "negative"}>{t.pnl_pts > 0 ? "+" : ""}{t.pnl_pts} pts</td>
                    <td style={{ color: t.r_multiple >= 0 ? "#34d399" : "#f87171" }}>{(t.r_multiple || 0).toFixed(1)}R</td>
                    <td style={{ color: "#34d399", fontSize: 10 }}>{t.max_favorable_pts}</td>
                    <td style={{ color: "#f87171", fontSize: 10 }}>{t.max_adverse_pts}</td>
                    <td><span style={{ color: t.result === "WIN" ? "#34d399" : "#f87171", fontWeight: 700 }}>{t.result || "?"}</span></td>
                    <td style={{ fontSize: 10 }}>{t.exit_reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!status?.active && closedTrades.length === 0 && pending.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "var(--btn-border)" }}>
          <p>Click <strong>Start Scanner</strong> to begin FVG paper trading on NIFTY.</p>
          <p style={{ fontSize: 12 }}>Arms a limit at the 50% level of fresh trend-aligned gaps; fills on a retrace. Scans every 5 min during market hours. Persists across refreshes.</p>
        </div>
      )}
    </div>
  );
}
